import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import 'antd/dist/reset.css';

import ProtectedRoute from './components/ui/ProtectedRoute';
import Login from './pages/auth/Login';
import MainLayout from './components/layout/MainLayout';
import Dashboard from './pages/dashboard/Dashboard';
import UsersList from './pages/users/UsersList';
import UserEdit from './pages/users/UserEdit';
import CampaignsList from './pages/campaigns/CampaignsList';
import CampaignDetail from './pages/campaigns/CampaignDetail';
import Settings from './pages/settings/Settings';
import TranslationsList from './pages/translations/TranslationsList';
import LanguageTranslations from './pages/translations/LanguageTranslations';
import TranslationsManager from './pages/translations/TranslationsManager';
import ProjectsList from './pages/translations/ProjectsList';
import ProjectTranslations from './pages/translations/ProjectTranslations';
import NotFound from './pages/error/NotFound';
import Forbidden from './pages/error/Forbidden';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={ruRU}>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              
              {/* Users routes - Admin+ only */}
              <Route path="users" element={
                <ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}>
                  <UsersList />
                </ProtectedRoute>
              } />
              <Route path="users/create" element={
                <ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}>
                  <UserEdit />
                </ProtectedRoute>
              } />
              <Route path="users/:id/edit" element={
                <ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}>
                  <UserEdit />
                </ProtectedRoute>
              } />
              
              {/* Campaigns routes - All authenticated users */}
              <Route path="campaigns" element={<CampaignsList />} />
              <Route path="campaigns/:id" element={<CampaignDetail />} />
              
              {/* Translations routes - Translator+ only */}
              <Route path="translations" element={
                <ProtectedRoute roles={['TRANSLATOR', 'ADMIN', 'SUPERADMIN']}>
                  <TranslationsList />
                </ProtectedRoute>
              } />
              <Route path="translations/languages/:id" element={
                <ProtectedRoute roles={['TRANSLATOR', 'ADMIN', 'SUPERADMIN']}>
                  <LanguageTranslations />
                </ProtectedRoute>
              } />
              <Route path="translations/manager" element={
                <ProtectedRoute roles={['TRANSLATOR', 'ADMIN', 'SUPERADMIN']}>
                  <TranslationsManager />
                </ProtectedRoute>
              } />
              <Route path="translations/projects" element={
                <ProtectedRoute roles={['TRANSLATOR', 'ADMIN', 'SUPERADMIN']}>
                  <ProjectsList />
                </ProtectedRoute>
              } />
              <Route path="translations/projects/:projectId" element={
                <ProtectedRoute roles={['TRANSLATOR', 'ADMIN', 'SUPERADMIN']}>
                  <ProjectTranslations />
                </ProtectedRoute>
              } />
              
              {/* Settings route */}
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Error routes */}
            <Route path="/forbidden" element={<Forbidden />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </ConfigProvider>
    </QueryClientProvider>
  );
};

export default App;
